import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "game-emblem ai-emblem",
  "aria-hidden": "true"
}
const _hoisted_2 = {
  key: 2,
  class: "game-emblem material-icons room-icon",
  "aria-hidden": "true"
}
const _hoisted_3 = { class: "game-info" }
const _hoisted_4 = ["title"]
const _hoisted_5 = {
  key: 0,
  class: "ai-title"
}
const _hoisted_6 = {
  key: 1,
  class: "host-name"
}
const _hoisted_7 = { class: "game-details" }
const _hoisted_8 = {
  key: 0,
  class: "ai-meta"
}
const _hoisted_9 = {
  key: 0,
  class: "ai-open-seat"
}
const _hoisted_10 = { class: "ai-language" }
const _hoisted_11 = {
  key: 1,
  class: "ai-mode"
}
const _hoisted_12 = ["aria-label"]
const _hoisted_13 = {
  key: 0,
  "aria-hidden": "true"
}
const _hoisted_14 = {
  key: 1,
  class: "human-status"
}
const _hoisted_15 = {
  key: 0,
  "aria-hidden": "true"
}
const _hoisted_16 = {
  key: 2,
  class: "ai-date"
}

import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import type { TRoomInfo } from '@avalon/types';
import { useUserProfile } from '@/helpers/composables';
import OptionsPreview from '@/components/view/information/OptionsPreview.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'LobbyRoom',
  props: {
    game: {}
  },
  setup(__props: any) {

const props = __props;
const hostID = computed(() => props.game.hostID);
const { userName } = useUserProfile(hostID);
const game = toRef(props, 'game');
const { locale, t } = useI18n();
const aiLanguageLabel = computed(
  () =>
    ({ en: 'English', ru: 'Русский', 'zh-tw': '繁體中文（台灣）', es: 'Español', vi: 'Tiếng Việt' })[
      game.value.aiLanguage || 'en'
    ],
);
const aiDate = computed(() =>
  new Date(game.value.startAt || game.value.createAt).toLocaleString(locale.value, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }),
);
const aiStatusLabel = computed(() => {
  if (game.value.aiStatus === 'finished' && game.value.result?.winner)
    return t(`aiArena.${game.value.result.winner === 'good' ? 'goodWon' : 'evilWon'}`);
  return t(`aiArena.${game.value.aiStatus || (game.value.result ? 'finished' : 'ready')}`);
});
const aiTitle = computed(
  () => game.value.aiTitle?.replace(/ vs \d+ Bots$/, '') || t('aiArena.tagline', { count: game.value.players }),
);
const aiRosterLabel = computed(() => {
  const humans = game.value.aiHumanCount ?? (game.value.aiTitle ? 1 : 0);
  const bots = game.value.aiBotCount ?? game.value.players - humans;
  if (humans > 1) return t('aiArena.cardHumanRoster', { humans, bots });
  return t(humans === 1 ? 'aiArena.mixedPlayerCount' : 'aiArena.playerCount', { count: bots });
});
const aiModeLabel = computed(() =>
  game.value.aiBeginner
    ? t('aiArena.cardBeginner')
    : game.value.aiBotDifficulty
      ? t(game.value.aiBotDifficulty === 'smart' ? 'aiArena.cardSmart' : 'aiArena.cardRegular')
      : '',
);
const aiAction = computed(() => t(game.value.aiStatus === 'running' ? 'aiArena.cardWatch' : 'mainPage.viewGame'));
const canJoin = computed(
  () => !game.value.ai && !game.value.result && game.value.state === 'created' && game.value.players < 10,
);
const hasOptions = computed(() =>
  [...Object.values(game.value.options.roles), ...Object.values(game.value.options.addons)].some(Boolean),
);
const status = computed(() => {
  if (game.value.result) return 'finished';
  if (game.value.state === 'locked') return 'starting';
  if (game.value.state === 'started') return 'inProgress';
  if (game.value.players >= 10) return 'full';
  return game.value.options.features?.lookingForPlayers ? 'lookingForPlayers' : 'waiting';
});
const isRecruiting = computed(() => canJoin.value && status.value === 'lookingForPlayers');
const action = computed(() => (game.value.result ? 'viewGame' : canJoin.value ? 'join' : 'watch'));

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createBlock(_component_RouterLink, {
    class: _normalizeClass(["game", { 'game--recruiting': isRecruiting.value, 'game--ai': game.value.ai }]),
    to: { name: 'room', params: { uuid: game.value.uuid } },
    "aria-label": 
      game.value.ai
        ? `${game.value.aiTitle || _ctx.$t('aiArena.title')}. ${_ctx.$t('aiArena.language', { language: aiLanguageLabel.value })}. ${aiStatusLabel.value}. ${aiAction.value}`
        : `${_ctx.$t(`mainPage.${action.value}`)} — ${_unref(userName)}. ${_ctx.$t(`mainPage.${status.value}`)}`
    
  }, {
    default: _withCtx(() => [
      (game.value.ai)
        ? (_openBlock(), _createElementBlock("span", _hoisted_1, _cache[0] || (_cache[0] = [
            _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              width: "22",
              height: "22",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "1.7"
            }, [
              _createElementVNode("rect", {
                x: "4",
                y: "6",
                width: "16",
                height: "15",
                rx: "5"
              }),
              _createElementVNode("path", { d: "M12 6V3M1 12v4m22-4v4M9 17h6" }),
              _createElementVNode("circle", {
                cx: "9",
                cy: "12",
                r: "1",
                fill: "currentColor"
              }),
              _createElementVNode("circle", {
                cx: "15",
                cy: "12",
                r: "1",
                fill: "currentColor"
              })
            ], -1)
          ])))
        : (game.value.result?.winner)
          ? (_openBlock(), _createElementBlock("span", {
              key: 1,
              class: _normalizeClass(["game-emblem", `${game.value.result.winner}-loyalty-icon`]),
              "aria-hidden": "true"
            }, null, 2))
          : (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(game.value.result ? 'flag' : canJoin.value ? 'meeting_room' : 'sports_esports'), 1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          class: "game-heading",
          title: game.value.ai ? aiTitle.value : _unref(userName)
        }, [
          (game.value.ai)
            ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(aiTitle.value), 1))
            : (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref(userName)), 1))
        ], 8, _hoisted_4),
        _createElementVNode("div", _hoisted_7, [
          (game.value.ai)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                _createElementVNode("span", null, _toDisplayString(aiRosterLabel.value), 1),
                (game.value.aiOpenSeats)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(_ctx.$t('aiArena.cardFriendSeatOpen')), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("span", _hoisted_10, _toDisplayString(aiLanguageLabel.value), 1),
                (aiModeLabel.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(aiModeLabel.value), 1))
                  : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createElementBlock("span", {
                key: 1,
                class: "players-amount",
                "aria-label": `${game.value.players} ${_ctx.$t('mainPage.players')}`
              }, [
                _cache[1] || (_cache[1] = _createElementVNode("span", {
                  class: "material-icons",
                  "aria-hidden": "true"
                }, "group", -1)),
                _createTextVNode(" " + _toDisplayString(game.value.state === 'created' && !game.value.result ? `${game.value.players}/10` : game.value.players), 1)
              ], 8, _hoisted_12)),
          (hasOptions.value)
            ? (_openBlock(), _createBlock(OptionsPreview, {
                key: 2,
                linked: false,
                "max-view": 5,
                roles: game.value.options.roles,
                addons: game.value.options.addons
              }, null, 8, ["roles", "addons"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _createElementVNode("span", {
        class: _normalizeClass(["game-status", {
        'game-status--recruiting': isRecruiting.value,
        'game-status--playing': !game.value.ai && game.value.state === 'started' && !game.value.result,
      }])
      }, [
        (game.value.ai)
          ? (_openBlock(), _createElementBlock("span", {
              key: 0,
              class: _normalizeClass(["ai-status", { 'ai-status--live': game.value.aiStatus === 'running' }])
            }, [
              (game.value.aiStatus === 'running')
                ? (_openBlock(), _createElementBlock("i", _hoisted_13))
                : (game.value.result?.winner)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 1,
                      class: _normalizeClass(`${game.value.result.winner}-loyalty-icon`),
                      "aria-hidden": "true"
                    }, null, 2))
                  : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(aiStatusLabel.value), 1)
            ], 2))
          : (_openBlock(), _createElementBlock("span", _hoisted_14, [
              (isRecruiting.value || (game.value.state === 'started' && !game.value.result))
                ? (_openBlock(), _createElementBlock("i", _hoisted_15))
                : _createCommentVNode("", true),
              _createTextVNode(_toDisplayString(_ctx.$t(`mainPage.${status.value}`)), 1)
            ])),
        (game.value.result || (game.value.ai && ['finished', 'stopped'].includes(game.value.aiStatus || '')))
          ? (_openBlock(), _createElementBlock("small", _hoisted_16, _toDisplayString(aiDate.value), 1))
          : _createCommentVNode("", true)
      ], 2),
      _createElementVNode("span", {
        class: _normalizeClass(["room-link", { 'room-link--join': canJoin.value }])
      }, [
        _createTextVNode(_toDisplayString(game.value.ai ? aiAction.value : _ctx.$t(`mainPage.${action.value}`)) + " ", 1),
        _cache[2] || (_cache[2] = _createElementVNode("span", { "aria-hidden": "true" }, "→", -1))
      ], 2)
    ]),
    _: 1
  }, 8, ["class", "to", "aria-label"]))
}
}

})